import { RootState } from '../../../../../../../../init-store';
import {
    PARTICIPANT_LIST_COLUMNS,
    PARTICIPANT_LIST_ITEMS_PER_PAGE,
    PARTICIPANT_LIST_ROWS,
} from '../../../../../../../shared/features/room-agent/dtmf/participant-list/constants';
import { selectParticipantRenderState } from '../../../../../../../shared/features/room-agent/dtmf/participant-list/selectors';
import { ParticipantState } from '../../../../../types';
import { safeDrawMedia } from '../../draw-helpers';
import { getIconImage, getPngImage } from '../../draw-images';
import { FONT_FAMILY, TEXT_COLOR_PRIMARY } from '../constants';
import { Area, Coordinates, Drawer } from '../types';
import { SCALE } from './constants';

const PARTICIPANTS_FONT_SIZE = Math.round(24 * SCALE);
const PARTICIPANTS_FONT = `${PARTICIPANTS_FONT_SIZE}px ${FONT_FAMILY}`;

const NAME_WIDTH = Math.round(300 * SCALE);
const ICON_SIZE = Math.round(26 * SCALE);
const ICON_MARGIN_RIGHT = Math.round(16 * SCALE);

const PARTICIPANT_ITEM_WIDTH = NAME_WIDTH + 2 * (ICON_SIZE + ICON_MARGIN_RIGHT);
const PARTICIPANT_ITEM_HEIGHT = ICON_SIZE;
const PARTICIPANT_ITEM_MARGIN_BOTTOM = Math.round(11 * SCALE);
export const PARTICIPANTS_WIDTH = PARTICIPANT_LIST_COLUMNS * PARTICIPANT_ITEM_WIDTH;
export const PARTICIPANTS_HEIGHT =
    PARTICIPANT_LIST_ROWS * PARTICIPANT_ITEM_HEIGHT + PARTICIPANT_ITEM_MARGIN_BOTTOM * (PARTICIPANT_LIST_ROWS - 1);

export class ParticipantsDrawer implements Drawer {
    private area: Area;
    private coordinates: Coordinates;
    private normalizedNames = new Map<string, string>();

    constructor(area: Area) {
        this.area = area;
        this.coordinates = { x: this.area.x, y: this.area.y };
    }

    normalizeName(ctx: CanvasRenderingContext2D, name: string) {
        ctx.textBaseline = 'top';
        ctx.font = PARTICIPANTS_FONT;

        const textMetrics = ctx.measureText(name);

        if (textMetrics.width > NAME_WIDTH) {
            const charLength = textMetrics.width / name.length;
            const charCapacity = Math.floor(NAME_WIDTH / charLength) - 1;

            return `${name.slice(0, charCapacity).trim()}...`;
        }

        return name;
    }

    getNormalizedName(ctx: CanvasRenderingContext2D, name: string): string {
        if (!this.normalizedNames.has(name)) {
            this.normalizedNames.set(name, this.normalizeName(ctx, name));
        }

        return this.normalizedNames.get(name) || '';
    }

    drawReaction(ctx: CanvasRenderingContext2D, participant: ParticipantState, x: number, y: number) {
        const reactionType = participant.reaction?.reactionType;

        if (!reactionType) {
            return;
        }

        const area = { x, y, width: ICON_SIZE, height: ICON_SIZE };
        const image = getPngImage(reactionType);
        const options = { mode: 'contain' } as const;

        safeDrawMedia(ctx, area, image, options);
    }

    drawMic(ctx: CanvasRenderingContext2D, participant: ParticipantState, x: number, y: number) {
        safeDrawMedia(
            ctx,
            { x, y, width: ICON_SIZE, height: ICON_SIZE },
            getIconImage({
                name: participant.audio?.muted ? 'micMuted' : 'micOn',
                iconColor: TEXT_COLOR_PRIMARY,
            }),
            { mode: 'contain' }
        );
    }

    draw(ctx: CanvasRenderingContext2D, state: RootState) {
        const { participantListChunk } = selectParticipantRenderState(state);
        const maxItems = Math.min(participantListChunk.length, PARTICIPANT_LIST_ITEMS_PER_PAGE);

        for (let i = 0; i < maxItems; i++) {
            const participant = participantListChunk[i];
            const normalizedName = this.getNormalizedName(ctx, participant.displayName || '');
            const col = Math.floor(i / PARTICIPANT_LIST_ROWS);
            const row = i % PARTICIPANT_LIST_ROWS;
            const x = this.coordinates.x + col * PARTICIPANT_ITEM_WIDTH;
            const y = this.coordinates.y + row * (PARTICIPANT_ITEM_HEIGHT + PARTICIPANT_ITEM_MARGIN_BOTTOM);

            this.drawReaction(ctx, participant, x, y);
            this.drawMic(ctx, participant, x + ICON_SIZE + ICON_MARGIN_RIGHT, y);

            ctx.textBaseline = 'top';
            ctx.font = PARTICIPANTS_FONT;
            ctx.fillStyle = TEXT_COLOR_PRIMARY;
            ctx.fillText(normalizedName, x + 2 * (ICON_SIZE + ICON_MARGIN_RIGHT), y + 4);
        }
    }
}
